<script setup lang="ts">
    import type { AccordeonComponentStoryblok as AccordeonComponentBlok } from '~storyblok-types/AccordeonComponentStoryblok';
    import type { AccordeonElementStoryblok } from '~storyblok-types/AccordeonElementStoryblok';
    import type { AccordeonEntryStoryblok } from '~storyblok-types/AccordeonEntryStoryblok';

    type LegacyAccordeonItem = AccordeonElementStoryblok & {
        Headline?: string;
    };

    const props = defineProps<{ blok: AccordeonComponentBlok }>();

    let resizeObserver: ResizeObserver | undefined;
    let intersectionObserver: IntersectionObserver | undefined;

    // Normalize legacy accordion items so older Storyblok content still renders via AccordeonEntry.
    const normalizeAccordeonItem = (item: AccordeonEntryStoryblok | LegacyAccordeonItem): AccordeonEntryStoryblok => {
        if (item.component === 'AccordeonEntry') {
            return item;
        }

        return {
            _uid: item._uid,
            component: 'AccordeonEntry',
            headline: item.Headline || '',
            headerHoverColor: item.hoverColor || 'default',
            content: [item]
        };
    };

    const accordeonItems = computed(() =>
        ((props.blok.AccordeonElements ?? []) as Array<AccordeonEntryStoryblok | LegacyAccordeonItem>).map(
            normalizeAccordeonItem
        )
    );

    const openUid = ref<string | null>(
        props.blok.openFirstElementOnLoad ? (accordeonItems.value[0]?._uid ?? null) : null
    );

    const containerRef = ref<HTMLElement | null>(null);

    const isSingleOpen = computed(() => Boolean(props.blok.allowOnlyOneOpen));

    const isItemOpen = (uid: string, index: number): boolean | undefined => {
        if (isSingleOpen.value) {
            return openUid.value === uid;
        }
        if (props.blok.openFirstElementOnLoad && index === 0) {
            return true;
        }
        return undefined;
    };

    const shouldUseEqualHeights = computed(() =>
        Boolean(props.blok.equalizeHeight && props.blok.alternateDesktopLayout)
    );

    const equalHeightSignature = computed(() =>
        accordeonItems.value.map((item) => `${item._uid}:${item.headline}`).join('|')
    );

    const getQuestionButtons = () => {
        const container = containerRef.value;
        if (!container) return [];

        return [...container.querySelectorAll('.accordeon-question')] as HTMLElement[];
    };

    const resetEqualHeights = () => {
        getQuestionButtons().forEach((el) => {
            el.style.height = 'auto';
        });
    };

    const setEqualHeights = () => {
        nextTick(() => {
            resetEqualHeights();
            const rawButtonEls = getQuestionButtons();
            if (!rawButtonEls.length) return;

            const rows = new Map<number, HTMLElement[]>();

            rawButtonEls.forEach((el) => {
                const top = el.offsetTop;
                if (!rows.has(top)) {
                    rows.set(top, []);
                }
                rows.get(top)!.push(el);
            });

            for (const rowElements of rows.values()) {
                const maxHeight = Math.max(...rowElements.map((el) => el.offsetHeight));

                rowElements.forEach((el) => {
                    el.style.height = `${maxHeight}px`;
                });
            }
        });
    };

    const handleToggle = (uid: string | null) => {
        openUid.value = openUid.value === uid ? null : uid;
    };

    const setupEqualHeightObservers = () => {
        if (!shouldUseEqualHeights.value || !containerRef.value) return;

        window.removeEventListener('resize', setEqualHeights);
        resizeObserver?.disconnect();
        intersectionObserver?.disconnect();

        intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;

                setEqualHeights();
                intersectionObserver?.disconnect();
            },
            { threshold: 0.1 }
        );

        intersectionObserver.observe(containerRef.value);

        resizeObserver = new ResizeObserver(setEqualHeights);
        resizeObserver.observe(containerRef.value);
        window.addEventListener('resize', setEqualHeights);
    };

    const cleanupEqualHeightObservers = () => {
        window.removeEventListener('resize', setEqualHeights);
        resizeObserver?.disconnect();
        intersectionObserver?.disconnect();
        resizeObserver = undefined;
        intersectionObserver = undefined;
    };

    onMounted(() => {
        nextTick(() => {
            setupEqualHeightObservers();
        });
    });

    watch(shouldUseEqualHeights, (shouldUse) => {
        if (shouldUse) {
            nextTick(() => {
                setupEqualHeightObservers();
                setEqualHeights();
            });
            return;
        }

        cleanupEqualHeightObservers();
        resetEqualHeights();
    });

    watch(
        equalHeightSignature,
        () => {
            if (shouldUseEqualHeights.value) {
                setEqualHeights();
            }
        },
        { flush: 'post' }
    );

    onBeforeUnmount(() => {
        cleanupEqualHeightObservers();
    });
</script>

<template>
    <ul
        ref="containerRef"
        v-editable="props.blok"
        class="accordeon-list"
        :class="{ '--alternate-desktop-layout': props.blok.alternateDesktopLayout }"
    >
        <li
            v-for="(childBlok, index) in accordeonItems"
            :key="childBlok._uid"
        >
            <StoryblokComponentWrapper
                :blok="childBlok"
                :is-open="isItemOpen(childBlok._uid, index)"
                :allow-only-one-open="isSingleOpen"
                @toggle="handleToggle(childBlok._uid)"
            />
        </li>
    </ul>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/accordeon.scss';
</style>
